import React, { useEffect, useState, useCallback } from "react";
import { StyledEngineProvider } from '@mui/material/styles';
import PoDetailsTable from "./PoDetailsTable";
import CreatePO from "./CreatePo";
import { createPO, fetchDetailByID, fetchPO, fetchPoDetailByID, updatePO } from "../../services/purchaseOrder/purchaseOrder";
import { toast } from '../../components/AppToast';
import '../../utils/patchResizeObserver';
import useFormatForInput from "../../components/commonDateTimeConverter";
// import ASNView from "./ASNView"; // 🔗 simple placeholder view
import ASNManagement from "../asn/ASNManagement"; // 🔗 full ASN management view
import { useSelector } from "react-redux";
import { usePermission } from "../../hooks/usePermission";
import { transformSinglePO } from "../../utils/transformSinglePO";


const PO_STATUS_ENUM = {
  new: 1,
  approved: 2,
  pending: 3,
  invoiced: 4,
  completed: 5,
  canceled: 6,
  other: 0,
  inprocess: 7,
};



const PoDetails = () => {

  // 🔐 PERMISSION CHECKS (ONLY ADDITION)
  const { hasPermission } = usePermission();  
  const canViewPO = hasPermission("poDetails", "VIEW");
  const canAddPO = hasPermission("poDetails", "ADD");
  const canEditPO = hasPermission("poDetails", "UPDATE");

  const [modalMode, setModalMode] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [tableData, setTableData] = useState([]);

  // 🔗 deep view: for ASN or other linked entities
  const [deepView, setDeepView] = useState(null); // e.g. { type: 'ASN', id: 'ASN303' }
  const [asnID, setAsnID] = useState(null); // e.g. { type: 'ASN', id: 'ASN303' }

  const { userDetail } = useSelector((state) => state.login);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const formatForInput = useFormatForInput();

  // Create button click
  // 🔐 If user cannot VIEW → block entire screen (ADDED)

  const handleNewClick = () => {

    // 🔐 Block CREATE if user has no ADD permission (ADDED)
    if (!canAddPO) return;

    setCurrentRow(null);
    setModalMode('create');
  };

  const handleCancel = () => setModalMode(null);

  const handleView = async (row) => {

    const transformed = await handleTransform(row);

    if (!canViewPO) return;

    setCurrentRow(transformed);
    setModalMode('view');
  };

  const parseDate = (input) => {
    if (input instanceof Date && !isNaN(input)) return input;
    if (typeof input === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(input)) {
      const [day, month, year] = input.split("/").map(Number);
      return new Date(year, month - 1, day);
    }
    return null;
  };

  const handleTransform = async (po) => {
    // Get Po Data From API By ID
    console.log("po detilss po", po);

    const res = await fetchPoDetailByID(po._id);

    const newObj = transformSinglePO((res));
    const receiptDate = parseDate(newObj.expectedReceiptDate);
    if (receiptDate) newObj.expectedReceiptDate = receiptDate.toISOString().split('T')[0];
    newObj.purchaseOrderDate = formatForInput(newObj.purchaseOrderDate);

    console.log("newObjnewObj", newObj);


    return newObj;
  };

  const handleEdit = async (row) => {

    // 🔐 Block edit if user has no UPDATE permission (ADDED)
    if (!canEditPO) {
      toast.error("You do not have permission to edit PO");
      return;
    }
    const transformed = await handleTransform(row);
    setCurrentRow(transformed);
    setModalMode('edit');
  };

  const handleSubmit = async (formData) => {
    console.log("Form submitted with data:", formData);

    // 🔐 Extra safety: block actions if no permission (ADDED)
    if (modalMode === 'create' && !canAddPO) {
      toast.error("You do not have permission to create PO");
      return;
    }
    if (modalMode === 'edit' && !canEditPO) {

      toast.error("You do not have permission to edit PO");
      return;
    }

    const newPO = {
      id: Date.now(),
      purchaseOrder: formData.purchaseOrder || `PO${Date.now()}`,
      buyerOwner: formData.buyerName || 'Default',
      status: 1,
      type: formData.type || 'Standard',
      purchaseOrderDate: new Date().toLocaleDateString(),
      expectedReceiptDate: formData.expectedReceiptDate || new Date().toLocaleDateString(),
      supplier: formData.supplier || '',
      externalPoNumber: formData.externalPoNumber || '',
      supplierReference: formData.supplierReference || '',
      openQuantity: formData.detailItems?.length || '',
      buyersReference: formData.buyersReference || '',
      otherReference: formData.otherReference || '',
      carrier: formData.carrier || '',
      purchasingOfficeLocation: formData.purchasingOfficeLocation || '',
      buyerName: formData.buyerName || '',
      detailItems: formData.detailItems || []
    };

    const payload = {
      statusHistory: null,
      audit: null,
      tenantId: userDetail?.tenantId?._id || formData.tenantId || null,
      partnerId: formData.partnerId || null,
      createdBy: formData.createByUser || null,
      updatedBy: formData.editedByUser || null,

      purchase_order: {
        orderId: formData.purchaseOrder || '',
        status: formData.status === "In Progress" ? 7 : 1,
        type: formData.type || 'Standard',
        externalPoNumber: formData.externalPoNumber || '',
        buyerName: formData.buyerName || '',
        expectedReceiptDate: formData.expectedReceiptDate || '',
        purchasingOfficeLocation: formData.purchasingOfficeLocation || '',
        supplierReference: formData.supplierReference || '',
        externalPoRef2: formData.externalPoRef2 || '',
        otherReference: formData.otherReference || '',
        openQuantity: parseInt(formData.openQuantity) || '',
        closedDate: formData.closedDate || '',
        source: formData.source || '',
        validityDate: formData.validityDate || '',
      },

      buyer: {
        buyer: formData.buyerOwner || '',
        address: {
          addressLine1: formData.buyerAddressLine1 || '',
          addressLine2: formData.buyerAddressLine2 || '',
          addressLine3: formData.buyerAddressLine3 || '',
          addressLine4: formData.buyerAddressLine4 || '',
          city: formData.buyerCity || '',
          state: formData.buyerState || '',
          country: formData.buyerCountry || '',
          zip: formData.buyerZip || '',
          vatNumber: formData.buyerVat || '',
          buyersReference: formData.buyersReference || ''
        }
      },


      supplier: {
        supplier: formData.supplierId || formData.supplier || '',
        address: {
          addressLine1: formData.addressLine1 || '',
          addressLine2: formData.addressLine2 || '',
          addressLine3: formData.addressLine3 || '',
          addressLine4: formData.addressLine4 || '',
          city: formData.city || '',
          state: formData.state || '',
          country: formData.country || '',
          zip: formData.zip || '',
          vatNumber: formData.vat || '',
          sellersReference: formData.sellersReference || '' 
        }
      },

      misc: {
        poHeaderUDF1: formData.poHeaderUdf1 || '',
        poHeaderUDF2: formData.poHeaderUdf2 || '',
        poHeaderUDF3: formData.poHeaderUdf3 || '',
        poHeaderUDF4: formData.poHeaderUdf4 || '',
        poHeaderUDF5: formData.poHeaderUdf5 || '',
        apportionRule: formData.apportionRule || '',
        notes: formData.notes || ''
      },

      loading: {
        loadingDate: formData.loadingDate || '',
        carrier: formData.carrier || '',
        placeOfLoading: formData.placeOfLoading || '',
        placeOfDelivery: formData.placeOfDelivery || '',
        placeOfDischarge: formData.placeOfDischarge || '',
        countryOfOrigin: formData.countryOfOrigin || '',
        countryOfDestination: formData.countryOfDestination || '',
        termsOfDelivery: formData.termsOfDelivery || '',
        termsOfPayment: formData.termsOfPayment || ''
      },

      udf: {
        purchaseOrderTextUDF1: formData.customField1 || '',
        purchaseOrderTextUDF2: formData.customField2 || '',
        purchaseOrderTextUDF3: formData.customField3 || '',
        purchaseOrderTextUDF4: formData.customNotes || '',
        purchaseOrderTextUDF5: '',
        purchaseOrderNumericUDF1: formData.customField5 || '',
        purchaseOrderNumericUDF2: '',
        purchaseOrderNumericUDF3: '',
        purchaseOrderNumericUDF4: '',
        purchaseOrderNumericUDF5: '',
        purchaseOrderDateUDF1: formData.customField4 || '',
        purchaseOrderDateUDF2: formData.createDate || '',
        purchaseOrderDateUDF3: formData.editDate || '',
        purchaseOrderDateUDF4: '',
        purchaseOrderDateUDF5: '',
        purchaseOrderCodeListUDF1: '',
        purchaseOrderCodeListUDF2: '',
        purchaseOrderCodeListUDF3: '',
        purchaseOrderCodeListUDF4: '',
        purchaseOrderCodeListUDF5: ''
      },

      lineItems: formData.detailItems?.map((item, index) => ({
        detail: {
          lineNumber: index + 1,
          purchaseOrder: formData.purchaseOrder || '',
          status: item.status || '',
          qcRequired: item.qcRequired || "",
          skuId: item.skuId || item.item || '',
          receivedQty: item.receivedQty || 0,
          qcAutoAdjust: item.qcAutoAdjust || false,
          description: item.description || '',
          supplierLot: item.supplierLot || '',
          pack: item.pack || '',
          lpnNo: item.lpnNo || item.lpn || '',
          batchNum: item.batchNum || item.batchNo || item.batchNumber || '',
          orderedQty: item.orderedQty || 0,
          openQty: item.openQty || 0,
          shortQty: item.shortQty || 0,
          itemPOTolerance: item.itemPOTolerance || 0,
          unitPrice: item.unitPrice || 0,
          discount: item.discount || 0,
          tax: item.tax || 0,
          totalPrice: item.totalPrice ?? null,
          uom: item.uom || '',
          itemCube: item.itemCube ?? null,
          itemWeight: item.itemWeight ?? null,
          serialDetails: Array.isArray(item.serialDetails) ? item.serialDetails : [],
        },
        udf: {
          poLineUdf1: item.poLineUdf1 || '',
          poLineUdf2: item.poLineUdf2 || '',
          poLineUdf3: item.poLineUdf3 || '',
          poLineUdf4: item.poLineUdf4 || '',
          poLineUdf5: item.poLineUdf5 || '',
          createdBy: item.createdBy || '',
          updatedBy: item.updatedBy || ''
        },
        extra_udf: {
          purchaseOrderTextUDF1: item.purchaseOrderTextUDF1 || '',
          purchaseOrderTextUDF2: item.purchaseOrderTextUDF2 || '',
          purchaseOrderTextUDF3: item.purchaseOrderTextUDF3 || '',
          purchaseOrderTextUDF4: item.purchaseOrderTextUDF4 || '',
          purchaseOrderTextUDF5: item.purchaseOrderTextUDF5 || '',
          purchaseOrderNumericUDF1: item.purchaseOrderNumericUDF1 || '',
          purchaseOrderNumericUDF2: item.purchaseOrderNumericUDF2 || '',
          purchaseOrderNumericUDF3: item.purchaseOrderNumericUDF3 || '',
          purchaseOrderNumericUDF4: item.purchaseOrderNumericUDF4 || '',
          purchaseOrderNumericUDF5: item.purchaseOrderNumericUDF5 || '',
          purchaseOrderDateUDF1: item.purchaseOrderDateUDF1 || '',
          purchaseOrderDateUDF2: item.purchaseOrderDateUDF2 || '',
          purchaseOrderDateUDF3: item.purchaseOrderDateUDF3 || '',
          purchaseOrderDateUDF4: item.purchaseOrderDateUDF4 || '',
          purchaseOrderDateUDF5: item.purchaseOrderDateUDF5 || '',
          purchaseOrderCodeListUDF1: item.purchaseOrderCodeListUDF1 || '',
          purchaseOrderCodeListUDF2: item.purchaseOrderCodeListUDF2 || '',
          purchaseOrderCodeListUDF3: item.purchaseOrderCodeListUDF3 || '',
          purchaseOrderCodeListUDF4: item.purchaseOrderCodeListUDF4 || '',
          purchaseOrderCodeListUDF5: item.purchaseOrderCodeListUDF5 || ''
        }
      })) || []
    };

    try {
      if (modalMode === 'edit') {
        const id = currentRow?._id || currentRow?.raw?._id || formData.id;
        const payloadToUpdate = { _id: id, ...payload };
        payloadToUpdate.lineItems.forEach(li => {
          li.purchaseOrderId = id;
        });
        await updatePO(payloadToUpdate);
        toast.success('PO updated successfully', { position: 'top-center', autoClose: 3000 });
        await fetchHandler(0, pageSize);
        setModalMode(null);
        setCurrentRow(null);
        return;
      }

      const response = await createPO(payload);
      if (response.purchaseOrder) {
        toast.success(`PO Number ${response.purchaseOrder} created successfully`, { position: "top-center", autoClose: 5000 });
      } else {
        toast.success('PO created successfully', { position: 'top-center', autoClose: 3000 });
      }

      await fetchHandler();
      setModalMode(null);

    } catch (err) { }
  };

  // -----------------------------
  // FETCH PO LIST (UNCHANGED)
  // -----------------------------

  // Map UI column IDs to backend field paths
  const FILTER_FIELD_MAP = {
    // 🔹 Action column (no direct backend field)
    // actions: null,

    // 🔹 Header / main PO fields
    purchaseOrder: "purchase_order.orderId",
    externalPoNumber: "purchase_order.externalPoNumber",
    buyerOwner: "buyer.buyer",
    buyerName: "purchase_order.buyerName",
    purchasingOfficeLocation: "purchase_order.purchasingOfficeLocation",
    expectedReceiptDate: "purchase_order.expectedReceiptDate",
    status: "purchase_order.status",
    type: "purchase_order.type",
    source: "source", // top-level source
    purchaseOrderDate: "createdAt", // as you had earlier (instead of purchase_order.purchaseOrderDate)

    // 🔹 Quantities / amounts
    totalOrderedQuantity: "purchase_order.totalOrderedQuantity",
    openQuantity: "purchase_order.openQuantity",
    totalWeight: "purchase_order.totalWeight",
    totalVolume: "purchase_order.totalVolume",
    totalPOPrice: "purchase_order.totalPOPrice",

    // 🔹 Supplier & buyer references
    buyersReference: "purchase_order.buyersReference",
    otherReference: "purchase_order.otherReference",
    supplierReference: "purchase_order.supplierReference",
    carrier: "purchase_order.carrier",

    // 🔹 ASN(s)
    asnNumbers: "asnNumbers", // assuming top-level asnNumbers array

    // 🔹 Status / dates
    closedDate: "purchase_order.closedDate",
    loadingDate: "purchase_order.loadingDate",
    editDate: "misc.updatedDate",
    // if you want PO header created date instead: "purchase_order.purchaseOrderDate"

    // 🔹 Buyer address
    buyerAddressLine1: "buyer.address.addressLine1",
    buyerCity: "buyer.address.city",
    buyerState: "buyer.address.state",
    buyerCountry: "buyer.address.country",

    // 🔹 Supplier address
    addressLine1: "supplier.address.addressLine1",
    city: "supplier.address.city",
    state: "supplier.address.state",
    country: "supplier.address.country",

    // 🔹 Misc header info
    poHeaderUdf1: "misc.poHeaderUDF1",
    poHeaderUdf2: "misc.poHeaderUDF2",
    poHeaderUdf3: "misc.poHeaderUDF3",
    poHeaderUdf4: "misc.poHeaderUDF4",
    poHeaderUdf5: "misc.poHeaderUDF5",
    apportionRule: "misc.apportionRule",
    notes: "misc.notes",
    termsOfDelivery: "misc.termsOfDelivery",
    termsOfPayment: "misc.termsOfPayment",

    // 🔹 Audit / created/updated by
    createByUser: "misc.createdBy",
    editedByUser: "misc.updatedBy",

    // 🔹 Locations (shipping / logistics)
    placeOfLoading: "purchase_order.placeOfLoading",
    countryOfOrigin: "purchase_order.countryOfOrigin",
    placeOfDelivery: "purchase_order.placeOfDelivery",
    countryOfDestination: "purchase_order.countryOfDestination",
    placeOfDischarge: "purchase_order.placeOfDischarge",

    // 🔹 UDF text fields (PO Text UDFs)
    // field names are customField1,2,4,5 on UI -> map to udf.textUDF*
    customField1: "udf.textUDF1",
    customField2: "udf.textUDF2",
    customField4: "udf.textUDF3",
    customField5: "udf.textUDF4",

    // 🔹 Item-level fields (from your existing map)
    skuId: "lineItems.detail.skuId",
    item: "lineItems.detail.skuId",
    description: "lineItems.detail.description",
    orderedQty: "lineItems.detail.orderedQty",
  };


  // Convert UI filters array -> API filters
  function mapUiFiltersToApiOLD(filters = []) {
    if (!Array.isArray(filters)) return [];

    return filters
      .map((f) => {
        if (!f || !f.columnId) return null;
        const apiField = FILTER_FIELD_MAP[f.columnId] || f.columnId; // fallback to same id
        // keep operator/value as-is; backend may expect { field, operator, value }
        return {
          columnId: apiField,
          operator: f.operator,
          value: f.value,
        };
      })
      .filter(Boolean);
  }


  function mapUiFiltersToApi(filters = []) {
    if (!Array.isArray(filters)) return [];

    return filters
      .map((f) => {
        if (!f || !f.columnId) return null;

        const apiField = FILTER_FIELD_MAP[f.columnId] || f.columnId;
        let value = f.value;

        // 🔥 STATUS SPECIAL HANDLING
        if (f.columnId === "status") {
          // UI -> "New", "Approved", etc.
          const normalized = String(f.value).trim().toLowerCase();
          value = PO_STATUS_ENUM[normalized];

          // agar mapping fail ho jaye to filter drop kar do
          if (value === undefined) return null;
        }

        return {
          columnId: apiField,
          operator: f.operator,
          value,
        };
      })
      .filter(Boolean);
  }



  const fetchHandler = useCallback(async (pageno, pageSizeno, filters = []) => {
    try {
      //  console.log("send these page, pageSize" , pageno, pageSizeno);

      console.log("fetchHandler called with page, pageSize, filters:", pageno, pageSizeno, filters);

      const apiFilters = mapUiFiltersToApi(filters);

      const allres = await fetchPO(pageno, pageSizeno, apiFilters);

      console.log("API response for fetchPO:", allres);



      const res = allres.data;

      const total = allres.meta.total;

      const transformPOData = (apiData = []) => {
        return apiData.map((po) => {
          const poHdr = po.purchase_order || {};
          const buyer = po.buyer || {};
          const buyerAddr = buyer.address || {};
          const supplier = po.supplier || {};
          const supplierAddr = supplier.address || {};
          const misc = po.misc || {};
          const loading = po.loading || {};
          const udf = po.udf || {};

          const detailItems = (po.lineItems || []).map((li, idx) => {
            const d = li.detail || {};
            const u = li.udf || {};
            const extra = li.extra_udf || {};
            const lineNumber = d.lineNumber ?? idx + 1;

            const createByVal = u.createdBy ?? li.createdBy ?? "";
            const editByVal = u.updatedBy ?? li.updatedBy ?? "";
            const createDateRaw = li.createdAt ?? u.createdAt ?? "";
            const editDateRaw = li.updatedAt ?? u.updatedAt ?? "";
            const notesVal = u.notes ?? li.notes ?? "";

            return {
              id: li._id || d._id || `line-${po._id}-${idx}`,
              lineNo: String(lineNumber).padStart(5, "0"),
              purchaseOrder: d.purchaseOrder || "",
              skuId: d.skuId,
              item: d.skuId,
              description: d.description || "",
              itemCube: d.itemCube ?? "",
              itemWeight: d.itemWeight ?? "",
              serialDetails: Array.isArray(d.serialDetails) ? d.serialDetails : [],
              supplierLot: d.supplierLot || "",
              pack: d.pack || "",
              lpnNo: d.lpnNo || d.lpn || "",
              batchNum: d.batchNum || d.batchNo || d.batchNumber || "",
              orderedQty: d.orderedQty ?? 0,
              openQty: d.openQty ?? 0,
              itemPOTolerance: d.itemPOTolerance ?? 0,
              unitPrice: d.unitPrice ?? 0,
              discount: d.discount ?? 0,
              tax: d.tax ?? 0,
              totalPrice: d.totalPrice ?? 0,
              receivedQty: d.receivedQty ?? 0,
              uom: d.uom || "",
              qcRequired: d.qcRequired === undefined ? false : d.qcRequired,
              qcAutoAdjust: d.qcAutoAdjust ?? false,
              status: li.status || "",
              poLineUdf1: u.poLineUDF1 ?? u.poLineUdf1 ?? "",
              poLineUdf2: u.poLineUDF2 ?? u.poLineUdf2 ?? "",
              poLineUdf3: u.poLineUDF3 ?? u.poLineUdf3 ?? "",
              poLineUdf4: u.poLineUDF4 ?? u.poLineUdf4 ?? "",
              poLineUdf5: u.poLineUDF5 ?? u.poLineUdf5 ?? "",
              createByUser: createByVal,
              createDate: createDateRaw ? new Date(createDateRaw).toISOString().slice(0, 16) : "",
              editByUser: editByVal,
              editDate: editDateRaw ? new Date(editDateRaw).toISOString().slice(0, 16) : "",
              notes: notesVal,
              purchaseOrderTextUDF1: extra.purchaseOrderTextUDF1 || "",
              purchaseOrderTextUDF2: extra.purchaseOrderTextUDF2 || "",
              purchaseOrderTextUDF3: extra.purchaseOrderTextUDF3 || "",
              purchaseOrderTextUDF4: extra.purchaseOrderTextUDF4 || "",
              purchaseOrderTextUDF5: extra.purchaseOrderTextUDF5 || "",
              raw: li,
            };
          });

          return {
            id: poHdr.orderId,
            _id: po._id,
            buyerName: poHdr.buyerName || "",
            purchaseOrder: poHdr.orderId || "",


            buyersReference: poHdr.buyersReference || "",   
            sellersReference: poHdr.sellersReference || "",

            externalPoNumber: poHdr.externalPoNumber || "-",
            purchasingOfficeLocation: poHdr.purchasingOfficeLocation || "",
            openQuantity: poHdr.openQuantity ?? "",
            totalPOPrice: poHdr.totalPOPrice ?? 0,
            source: poHdr.source || "",
            status: poHdr.status,
            type: poHdr.type || "Standard",
            totalWeight: poHdr.totalWeight ?? 0,
            totalVolume: poHdr.totalVolume ?? 0,
            totalOrderedQuantity: poHdr.totalOrderedQuantity ?? 0,
            buyerOwner: buyer.buyer || "",
            purchaseOrderDate: poHdr.purchaseOrderDate
              ? new Date(poHdr.purchaseOrderDate).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
              : "",
            expectedReceiptDate: poHdr.expectedReceiptDate ? new Date(poHdr.expectedReceiptDate).toLocaleDateString('en-IN') : "",
            closedDate: poHdr.closedDate ? new Date(poHdr.closedDate).toISOString().slice(0, 10) : "",
            otherReference: poHdr.otherReference || "",

            asnNumbers: (() => {
              const asnArr = po.asnHistory || po.purchase_order?.asnHistory || [];
              if (!Array.isArray(asnArr) || asnArr.length === 0) return "";
              return asnArr.map((a) => a?.asnNumber).filter(Boolean).join(", ");
            })(),

            buyerAddressLine1: buyerAddr.addressLine1 || "",
            buyerAddressLine2: buyerAddr.addressLine2 || "",
            buyerAddressLine3: buyerAddr.addressLine3 || "",
            buyerAddressLine4: buyerAddr.addressLine4 || "",
            buyerCity: buyerAddr.city || "",
            buyerCountry: buyerAddr.country || "",

            buyersReference: buyerAddr.buyersReference || "",
            buyerState: buyerAddr.state || "",
            buyerZip: buyerAddr.zip || "",
            buyerEmail: buyerAddr.email || "",
            buyerContact: buyerAddr.contact || "",

            supplier: supplier.supplier || "",
            addressLine1: supplierAddr.addressLine1 || "",
            addressLine2: supplierAddr.addressLine2 || "",
            addressLine3: supplierAddr.addressLine3 || "",
            addressLine4: supplierAddr.addressLine4 || "",
            sellersReference: supplierAddr.sellersReference || "",
            city: supplierAddr.city || "",
            country: supplierAddr.country || "",
            state: supplierAddr.state || "",
            zip: supplierAddr.zip || "",
            vat: supplierAddr.vatNumber || "",
            supplierReference: poHdr.otherReference || "",
            email: supplierAddr.email || "",
            contact: supplierAddr.contact || "",

            poHeaderUdf1: misc.poHeaderUDF1 || "",
            poHeaderUdf2: misc.poHeaderUDF2 || "",
            poHeaderUdf3: misc.poHeaderUDF3 || "",
            poHeaderUdf4: misc.poHeaderUDF4 || "",
            poHeaderUdf5: misc.poHeaderUDF5 || "",
            apportionRule: misc.apportionRule || "",
            notes: misc.notes || "",
            createByUser: misc.createdBy ?? po.createdBy ?? "",
            createDate: po.createdAt ? new Date(po.createdAt).toISOString() : (misc.createdAt ? new Date(misc.createdAt).toISOString() : ""),
            editedByUser: misc.updatedBy ?? po.updatedBy ?? "",
            editDate: po.updatedAt ? new Date(po.updatedAt).toISOString() : (misc.updatedAt ? new Date(misc.updatedAt).toISOString() : ""),

            loadingDate: loading.loadingDate ? new Date(loading.loadingDate).toISOString().slice(0, 10) : "",
            carrier: loading.carrier || "",
            placeOfLoading: loading.placeOfLoading || "",
            placeOfDelivery: loading.placeOfDelivery || "",
            placeOfDischarge: loading.placeOfDischarge || "",
            countryOfOrigin: loading.countryOfOrigin || "",
            countryOfDestination: loading.countryOfDestination || "",
            termsOfDelivery: loading.termsOfDelivery || "",
            termsOfPayment: loading.termsOfPayment || "",

            customField1: udf.purchaseOrderTextUDF1 || "",
            customField2: udf.purchaseOrderTextUDF2 || "",
            customField3: udf.purchaseOrderTextUDF3 || "",
            customNotes: udf.purchaseOrderTextUDF4 || "",
            customField5: udf.purchaseOrderNumericUDF1 ?? "",
            customField4: udf.purchaseOrderDateUDF1 ? new Date(udf.purchaseOrderDateUDF1).toISOString().slice(0, 10) : "",

            detailItems,
            raw: po,
          };
        });
      };

      const tableRows = transformPOData(res);
      setTableData(tableRows);
      setTotalCount(total);
      console.log("Data refreshed successfully");

    } catch (error) {
      console.error("Error fetching PO data:", error);
    }
  }, []);

  useEffect(() => {
    fetchHandler(page, pageSize);
  }, [fetchHandler, page, pageSize]);

  // 🔗 when a link in table is clicked
  const handleLinkOpenOLD = ({ entity, value }) => {
    console.log("handleLinkOpen called with:", { entity, value });
    if (entity === 'ASN') {
      setDeepView({ type: 'ASN', id: value });
      return;
    }
  };

  const handleLinkOpen = ({ entity, value, label, row, field, raw }) => {
    console.log("handleLinkOpen called with:", row.raw.asnHistory[0].asnId)
    console.log("handleLinkOpen called with:1111111", { value });



    // 👇 ASN case: yahan value === asnId
    if (entity === "ASN") {
      setDeepView({ type: "ASN", id: value });
      setAsnID(row.raw.asnHistory[0].asnId)
      return;
    }

    // future me aur bhi entity handle kar sakte ho
    // if (entity === "PO") {
    //   setDeepView({ type: "PO", id: value });
    //   return;
    // }
  };


  // Back from deep view
  const exitDeepView = () => setDeepView(null);

  return (
    <div className="card shadow-lg main-card" style={{ position: 'relative' }}>
      <div className="card-header bg-secondary text-white">
        <h4 className="mb-0 h6 fw-semibold">
          {modalMode
            ? modalMode === 'create'
              ? "Create Purchase Order"
              : modalMode === 'edit'
                ? "Edit Purchase Order"
                : "View Purchase Order"
            : deepView
              ? `${deepView.type} - View`
              : "Purchase Order Details"}
        </h4>
      </div>

      {/* Breadcrumbs only in modal or deep view */}
      {(modalMode || deepView) && (
        <nav aria-label="breadcrumb" className="px-3 py-2 border-bottom">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={() => {
                  setModalMode(null);
                  setCurrentRow(null);
                  setDeepView(null);
                }}
              >
                Purchase Orders
              </button>
            </li>
            <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
              {modalMode
                ? modalMode === "create" ? "Create PO" : modalMode === "edit" ? "Edit PO" : "View PO"
                : deepView?.type === 'ASN' ? `${deepView.id}` : ''}
            </li>
          </ol>
        </nav>
      )}

      <div className="card-body">
        <StyledEngineProvider injectFirst>
          {modalMode ? (
            <CreatePO
              mode={modalMode}
              readOnly={
                modalMode === 'view' ||
                (modalMode === 'edit' && !canEditPO)   // 🔐 EDIT BLOCK (ADDED)
              }
              initialValues={currentRow || {}}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
            />
          ) : deepView?.type === 'ASN' ? (
            <ASNManagement
              initialAsnNumber={String(deepView.id)}
              initialAsnId={String(asnID)}
              onCancel={exitDeepView}
              onDeepLinkNotFound={exitDeepView}
            />
          ) : (
            <PoDetailsTable
              initialRows={tableData}
              onView={handleView}
              onEdit={canEditPO ? handleEdit : undefined}
              showCreateButton={canAddPO}
              createButtonText="Create New PO"
              createButtonIcon="bi-plus-circle"
              onCreateClick={handleNewClick}
              onLinkOpen={handleLinkOpen}
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
                // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
                console.log("PoDetails received onDataRefresh:", { filters, page: p, pageSize: ps });
                // call your existing fetchHandler (we'll update it to accept filters)
                await fetchHandler(p, ps, filters);
              }}
            />

          )}
        </StyledEngineProvider>
      </div>
    </div>
  );
};

export default PoDetails;

